import type { Locale } from '../i18n/types';

/**
 * How a tenant wants numbers, dates and money shown. Every value here is per tenant, never global
 * (SRS §2.4, NFR-I18N-03); the defaults are tenant #1's.
 */
export interface FormatSettings {
  locale: Locale;
  timeZone: string;
  currency: string;
  /** Eastern Arabic-Indic digits (٠–٩) as a display option. Storage is always numeric. */
  numerals: 'western' | 'eastern';
}

export const DEFAULT_FORMAT_SETTINGS: FormatSettings = {
  locale: 'ar',
  timeZone: 'Asia/Damascus',
  currency: 'SYP',
  numerals: 'western',
};

const EASTERN_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'] as const;

/** Rewrites ASCII digits as Arabic-Indic ones. Applied at the very end of formatting, never before. */
export function toEasternDigits(value: string): string {
  return value.replace(/[0-9]/g, (digit) => EASTERN_DIGITS[Number(digit)]!);
}

/** Rewrites Arabic-Indic digits as ASCII ones: what someone types on an Arabic keyboard, read as a number. */
function toAsciiDigits(value: string): string {
  return value.replace(/[٠-٩]/g, (digit) => String(EASTERN_DIGITS.indexOf(digit as typeof EASTERN_DIGITS[number])));
}

function applyNumerals(value: string, settings: FormatSettings): string {
  return settings.numerals === 'eastern' ? toEasternDigits(value) : value;
}

/** Accepts what the API sends: an ISO instant, a `YYYY-MM-DD` date, or a `Date`. */
export function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  // A bare `YYYY-MM-DD` is a calendar date, not an instant. Parsing it as UTC midnight and rendering it in
  // the tenant's zone would shift it a day west of Greenwich, so it is built in local terms instead.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isDateOnly(value: string | Date | null | undefined): boolean {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/**
 * `D/M/YYYY`, always.
 *
 * This is not a stylistic preference. The client's own payment ledger lists 1/9 above 5/8 in descending
 * order, which only reads correctly as day-first (SRS §2.4). `Intl` is not used for the date part because
 * the `ar` locale would supply its own order and, on some platforms, its own separator.
 */
export function formatDate(value: string | Date | null | undefined, settings: FormatSettings): string {
  const date = toDate(value);
  if (!date) return '—';
  const parts = zonedParts(date, settings, isDateOnly(value));
  return applyNumerals(`${parts.day}/${parts.month}/${parts.year}`, settings);
}

/**
 * Reads a date typed day first, `D/M/YYYY`, as `YYYY-MM-DD`; null where it is not a real calendar date.
 *
 * The inverse of `formatDate` for a typed field: Arabic-Indic digits are read as well as ASCII ones, and a
 * `-`, `.` or space between the parts is taken for the `/` a phone keyboard may not have.
 */
export function parseDate(text: string): string | null {
  const ascii = toAsciiDigits(text.trim());
  const parts = /^(\d{1,2})[/.\- ](\d{1,2})[/.\- ](\d{4})$/.exec(ascii);
  if (!parts) return null;

  const [day, month, year] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
  // Date.UTC rolls 31/4 over to 1/5; a date that does not come back as typed does not exist.
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return `${parts[3]}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * A temperature typed in degrees Celsius, "37.5", "37,5" or "٣٧٫٥", as the integer tenths the API takes (375); null
 * where it is not a number with one decimal at most. Whether the reading is plausible is the API's to say.
 */
export function parseDeciCelsius(text: string): number | null {
  const ascii = toAsciiDigits(text.trim()).replace(/[,٫]/, '.');
  const parts = /^(\d{1,3})(?:\.(\d))?$/.exec(ascii);
  return parts ? Number(parts[1]) * 10 + Number(parts[2] ?? 0) : null;
}

/** `HH:MM`, 24-hour, in the tenant's zone. */
export function formatTime(value: string | Date | null | undefined, settings: FormatSettings): string {
  const date = toDate(value);
  if (!date) return '—';
  const parts = zonedParts(date, settings, false);
  return applyNumerals(`${parts.hour}:${parts.minute}`, settings);
}

export function formatDateTime(value: string | Date | null | undefined, settings: FormatSettings): string {
  const date = toDate(value);
  if (!date) return '—';
  return `${formatDate(value, settings)} ${formatTime(value, settings)}`;
}

/** The month and year, for period headings: `أيلول 2026`. */
export function formatMonth(value: string | Date | null | undefined, settings: FormatSettings): string {
  const date = toDate(value);
  if (!date) return '—';
  const formatted = new Intl.DateTimeFormat(settings.locale, {
    month: 'long', year: 'numeric', timeZone: settings.timeZone,
  }).format(date);
  return applyNumerals(formatted, settings);
}

/** A month's name alone, `تشرين الأول`, for a month field beside a year field. */
export function formatMonthName(month: number, settings: FormatSettings): string {
  // The middle of the month at noon UTC, so no timezone moves it into the next or the last.
  return new Intl.DateTimeFormat(settings.locale, { month: 'long', timeZone: 'UTC' })
    .format(new Date(Date.UTC(2000, month - 1, 15, 12)));
}

/** A year as written, in the tenant's digits and with no thousands separator: `2026`. */
export function formatYear(year: number, settings: FormatSettings): string {
  return applyNumerals(String(year), settings);
}

/** The weekday name, used by attendance and the daily report. */
export function formatWeekday(value: string | Date | null | undefined, settings: FormatSettings): string {
  const date = toDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat(settings.locale, {
    weekday: 'long',
    timeZone: isDateOnly(value) ? undefined : settings.timeZone,
  }).format(date);
}

function zonedParts(date: Date, settings: FormatSettings, dateOnly: boolean) {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
    // A calendar date carries no time of day, so converting it into a zone would be inventing one.
    ...(dateOnly ? {} : { timeZone: settings.timeZone }),
  });
  const parts = Object.fromEntries(formatter.formatToParts(date).map((p) => [p.type, p.value]));
  return {
    // The day and month lose their leading zero: `1/9/2026`, matching the client's ledger.
    day: String(Number(parts.day)),
    month: String(Number(parts.month)),
    year: parts.year ?? '',
    // `en-GB` writes midnight as 24 in some engines, which no time field takes.
    hour: String(Number(parts.hour ?? 0) % 24).padStart(2, '0'),
    minute: parts.minute ?? '00',
  };
}

/**
 * A recording's length as a clock reads it, `m:ss` — "1:05", never "65 s". Rounded to the nearest second,
 * and never negative. The digits follow the tenant's numeral setting like every other number.
 */
export function formatDuration(milliseconds: number, settings: FormatSettings): string {
  const seconds = Math.max(0, Math.round(milliseconds / 1000));
  const clock = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  return applyNumerals(clock, settings);
}

/** A plain number, grouped for the locale. */
export function formatNumber(value: number, settings: FormatSettings): string {
  return applyNumerals(new Intl.NumberFormat(settings.locale).format(value), settings);
}

/**
 * Bytes as megabytes, with a decimal only where the value is not whole: "15", "2.5". The same rounding as the
 * API's refusals (`LimitText.Megabytes`), so a hint and a refusal name the same number.
 */
export function formatMegabytes(bytes: number, settings: FormatSettings): string {
  return formatNumber(Math.round((bytes / (1024 * 1024)) * 10) / 10, settings);
}

/**
 * Money, from the integer minor units the API stores.
 *
 * Amounts are never held in floating point: SYP values reach seven digits and would lose precision
 * (SRS §2.4). Callers pass the integer and the currency code, and this is the only place that knows how
 * many minor units the currency has.
 */
export function formatMoney(
  minorUnits: number,
  settings: FormatSettings,
  currency: string = settings.currency,
): string {
  const fractionDigits = minorUnitDigits(currency);
  const value = minorUnits / 10 ** fractionDigits;
  const formatted = new Intl.NumberFormat(settings.locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
  return applyNumerals(formatted, settings);
}

/**
 * Whole currency units, as the integer minor units the API stores.
 *
 * The inverse of {@link formatMoney}, and deliberately next to it: an entry form that multiplies by a
 * hundred while the display divides by one is a hundredfold error in a fee ledger, and the only way the
 * two cannot disagree is for both to ask the same function how many minor units this currency has.
 */
export function toMinorUnits(amount: number, currency: string): number {
  return Math.round(amount * 10 ** minorUnitDigits(currency));
}

/**
 * Integer minor units as whole currency units, to fill an entry form with an amount already stored.
 *
 * The inverse of {@link toMinorUnits}, asking the same function for the exponent, so that saving a form it
 * filled in writes back exactly what was there.
 */
export function fromMinorUnits(minorUnits: number, currency: string): number {
  return minorUnits / 10 ** minorUnitDigits(currency);
}

/** ISO 4217 exponents for the currencies Daghfal expects. Everything else is assumed to have two. */
function minorUnitDigits(currency: string): number {
  switch (currency.toUpperCase()) {
    // The Syrian pound has a nominal two, but subunits have not circulated for years and the client's
    // ledger is written in whole pounds; showing `.00` on every line would be noise.
    case 'SYP':
    case 'JPY':
    case 'IQD':
      return 0;
    default:
      return 2;
  }
}

/** `YYYY-MM-DD` in the tenant's zone — the form every date-only field in the API takes. */
export function toIsoDate(date: Date, settings: FormatSettings): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit', timeZone: settings.timeZone,
    }).formatToParts(date).map((p) => [p.type, p.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/**
 * The first and last day of a calendar entry, `YYYY-MM-DD`.
 *
 * An all-day entry is stored from midnight to 23:59:59 UTC and the API counts its days in UTC (a closure's days are the
 * ones absence leaves out), so its days are read in UTC too: read in a zone east of Greenwich, its last day would come
 * out one late, and saving it back would lengthen it. An entry with times is read in the tenant's zone.
 */
export function calendarDays(
  entry: { startAt: string; endAt: string; allDay: boolean }, settings: FormatSettings,
): { from: string; to: string } {
  const day = (instant: string) => (entry.allDay
    ? new Date(instant).toISOString().slice(0, 10)
    : toIsoDate(new Date(instant), settings));
  return { from: day(entry.startAt), to: day(entry.endAt) };
}

/** Today, in the tenant's zone rather than the device's. */
export function todayIso(settings: FormatSettings): string {
  return toIsoDate(new Date(), settings);
}

/**
 * `HH:MM` in the tenant's zone, in ASCII digits: the value a time field holds, where `formatTime` is what a person
 * reads.
 */
export function timeOfDay(value: string | Date | null | undefined, settings: FormatSettings): string {
  const date = toDate(value);
  if (!date) return '';
  const parts = zonedParts(date, settings, false);
  return `${parts.hour}:${parts.minute}`;
}

/**
 * The instant a `YYYY-MM-DD` date and an `HH:MM` time name in the tenant's zone, as ISO: the inverse of `timeOfDay`.
 *
 * A teacher who types 10:30 means half past ten at the nursery, whatever zone the device is set to. The zone's
 * offset is read for the moment itself, so a date on either side of a daylight-saving change comes out right.
 */
export function atTimeOfDay(date: string, time: string, settings: FormatSettings): string {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  const [hour, minute] = time.split(':').map(Number) as [number, number];
  const wall = Date.UTC(year, month - 1, day, hour, minute);

  const offsetAt = (instant: number): number => {
    const parts = zonedParts(new Date(instant), settings, false);
    const zoned = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour), Number(parts.minute));
    return zoned - instant;
  };

  const guess = wall - offsetAt(wall);
  return new Date(wall - offsetAt(guess)).toISOString();
}

/**
 * A `YYYY-MM-DD` date, moved by whole days.
 *
 * The arithmetic is done in UTC deliberately. `new Date('2026-09-23T00:00:00')` — no zone suffix — is
 * parsed as *local* midnight, and `toISOString()` then converts it back to UTC, so anywhere east of
 * Greenwich a one-day step landed two days away and the day in between could not be reached at all. The
 * whole target market is east of Greenwich, so that is not an edge case.
 *
 * A date-only value has no zone to honour in the first place, which is why UTC is the arithmetic that
 * leaves it alone.
 */
export function shiftIsoDate(date: string, days: number): string {
  const moved = new Date(`${date}T00:00:00Z`);
  moved.setUTCDate(moved.getUTCDate() + days);
  return moved.toISOString().slice(0, 10);
}

/** A child's age, as years and months, for the profile header. */
export function ageInMonths(dateOfBirth: string | Date, on: Date = new Date()): number {
  const birth = toDate(dateOfBirth);
  if (!birth) return 0;
  let months = (on.getFullYear() - birth.getFullYear()) * 12 + (on.getMonth() - birth.getMonth());
  if (on.getDate() < birth.getDate()) months -= 1;
  return Math.max(0, months);
}

/**
 * A phone number in E.164, which is what the API stores and what sign-in expects.
 *
 * Returns null when the input cannot be one, so a caller can say so before spending a request — and
 * before a failed attempt counts towards the account lockout.
 */
export function normalisePhone(input: string, defaultCountryCode = '963'): string | null {
  // Arabic-Indic digits are accepted on the way in: a parent typing on an Arabic keyboard produces them.
  const western = toAsciiDigits(input);
  const digits = western.replace(/[^\d+]/g, '');
  if (!digits) return null;

  let national = digits.startsWith('+') ? digits.slice(1) : digits;
  if (national.startsWith('00')) national = national.slice(2);
  else if (!digits.startsWith('+') && national.startsWith('0')) {
    national = defaultCountryCode + national.slice(1);
  }

  return /^\d{8,15}$/.test(national) ? `+${national}` : null;
}

/** The digits of a phone number, grouped for reading, never for storage. */
export function formatPhone(e164: string | null | undefined, settings: FormatSettings): string {
  if (!e164) return '—';
  // Left-to-right mark: a bare `+963…` next to Arabic text renders with the plus on the wrong end.
  return applyNumerals(`‎${e164}`, settings);
}
